<template>
  <div class="votes-chart-container">
    <transition name="fade" mode="out-in">
      <InsightsLoading v-if="isLoading" centered/>
      <div v-else-if="hasError" class="text-center pt-8 text-red-700">Couldn't load voting trends</div>
      <div key="content" v-else>
        <GChart
          ref="gChartMain"
          class="votes-chart"
          type="ComboChart"
          :data="chartData"
          :options="chartOptions"
          :resizeDebounce="500"
          :events="chartEvents"
        />
        <GChart
          v-if="allTotals"
          ref="gChart"
          class="all-votes-chart"
          type="ComboChart"
          :data="zoomChartData"
          :options="zoomChartOptions"
          :resizeDebounce="500"
          :events="zoomChartEvents"
        />
        <div v-if="!forcedTimeInterval" class="dropdowns">
          <select :value="timeInterval" @input="$emit('update:timeInterval', $event.target.value)">
            <option value="monthly">Monthly</option>
            <option value="daily">Daily</option>
          </select>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import {GChart} from 'vue-google-charts';

export default {
  name: 'VotesChart',
  components: {
    GChart
  },
  props: {
    forcedTimeInterval: {
      type: String,
      required: false
    },
    timeInterval: {
      type: String,
      default: 'monthly'
    },
    totals: {
      type: Array,
      default: () => ([])
    },
    allTotals: {
      type: Array,
      required: false
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    height: {
      type: Number,
      default: 375
    },
    chartArea: {
      type: Number,
      default: 330
    },
    withScroll: {
      type: Boolean,
      default: false
    },
    hasError: {
      type: Boolean,
      default: false
    },
    allowTimeSelect: {
      type: Boolean,
      default: true
    },
    fullChart: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      chartEvents: this.allowTimeSelect ? {
        select: () => this.getRow(this.$refs.gChartMain.chartObject, this.chartData, 5)
      } : {},
      zoomChartEvents: {
        select: () => this.getRow(this.$refs.gChart.chartObject, this.zoomChartData)
      }
    };
  },
  computed: {
    processedTotals() {
      if ( !this.totals || this.fullChart ) {
        return this.totals;
      }
      return this.convertVotesToFractionOfMaxVotes(this.totals);
    },
    zoomChartData() {
      if (!this.allTotals) {
        return;
      }
      return [
        [{type: 'date', label: 'Date', id: 'Date'}, 'Sentiment', {type: 'string', role: 'tooltip', p: {html: true}}],
        ...(this.allTotals.map(([date, ...rest]) => ([dayjs(date).toDate(), ...rest])) || []) // turn date strings into date object so the axis is continuous
      ];
    },
    chartData() {
      if (!this.processedTotals) {
        return;
      }
      return [
        [
          {type: 'date', label: 'Date', id: 'Date'},
          'Up Votes',
          {type: 'string', role: 'tooltip', p: {html: true}},
          'Down Votes',
          {type: 'string', role: 'tooltip', p: {html: true}},
          'Sentiment %',
          {type: 'string', role: 'tooltip', p: {html: true}},
          '',
          {type: 'string', role: 'tooltip', p: {html: true}}
        ],
        ...(this.processedTotals.map(([date, ...rest]) => ([dayjs(date).toDate(), ...rest])) || []) // turn date strings into date object so the axis is continuous
      ];
    },
    finalTimeInterval() {
      return this.forcedTimeInterval || this.timeInterval || 'monthly';
    },
    zoomChartOptions() {
      return {
        allowHtml: true,
        animation: {
          duration: 300,
          easing: 'linear',
          startup: true
        },
        tooltip: {
          isHtml: true,
          ignoreBounds: true
        },
        backgroundColor: 'transparent',
        chartArea: {
          backgroundColor: '#eeeeee',
          height: 70
        },
        legend: {position: 'none'},
        height: 160,
        interpolateNulls: true,
        explorer: {
          axis: 'horizontal',
          keepInBounds: true,
          maxZoomIn: .1,
          maxZoomOut: 1
        },
        colors: ['#9a9a9a'],
        hAxis: {
          format: this.finalTimeInterval === 'monthly' ? 'MMM YYYY' : 'MMM d, YYYY'
        },
        vAxis: {
          title: '',
          viewWindow: {min: 0, max: 1, gridlines: {color: 'transparent'}},
          textPosition: 'none',
          visibleInLegend: false,
          gridlines: {
            count: 0
          }
        }
      };
    },
    // https://developers.google.com/chart/interactive/docs/gallery/combochart
    // Array will be automatically processed with visualization.arrayToDataTable function
    chartOptions() {
      return {
        animation: {
          duration: 800,
          easing: 'linear',
          startup: true
        },
        backgroundColor: 'transparent',
        allowHtml: true,
        height: this.height,
        chartArea: {
          height: this.chartArea
        },
        ...(this.withScroll && {
          explorer: {
            axis: 'horizontal',
            keepInBounds: true,
            maxZoomIn: .1,
            maxZoomOut: 1
          }
        }),
        bar: {
          groupWidth: '100%'
        },
        legend: {
          position: 'top',
          alignment: 'center',
          textStyle: {
            color: '#757575',
            fontSize: 14,
            fontName: 'Roboto'
          }
        },
        vAxes: {
          ...( this.fullChart ? {
            0: {
              title: 'Votes',
              viewWindow: {min: 0},
              format: '#,###',
              gridlines: {color: 'transparent', count: 4},
              titleTextStyle: {
                fontName: 'Roboto',
                fontSize: 14,
                italic: false
              }
            },
            1: {
              title: '% Sentiment',
              format: '#%',
              ticks: [0, .25, .5, .75, 1],
              viewWindow: {min: 0, max: 1},
              titleTextStyle: {
                fontName: 'Roboto',
                fontSize: 12,
                italic: false
              }
            },
            2: {
              title: '',
              ticks: [0, 1],
              viewWindow: {min: 0, max: 1}
            }
          } : {
            0: {
              title: '% Sentiment',
              format: '#%',
              ticks: [0, .25, .5, .75, 1],
              viewWindow: {min: 0, max: 1},
              titleTextStyle: {
                fontName: 'Roboto',
                fontSize: 12,
                italic: false
              }
            },
          } )
        },
        hAxis: {
          format: this.finalTimeInterval === 'monthly' ? 'MMM YYYY' : 'MMM d, YYYY',
          textStyle: {
            fontName: 'Roboto',
            fontSize: '14px',
            italic: false
          },
          gridlines: {color: 'transparent'}
        },
        seriesType: 'bars',
        series: {
          0: {targetAxisIndex: 0},
          1: {targetAxisIndex: 0},
          ...( this.fullChart ? {
            2: {targetAxisIndex: 1, type: 'line'},
            3: {targetAxisIndex: 2, visibleInLegend: false, color: 'black'}
          } : {
            2: {targetAxisIndex: 0, type: 'line'},
            3: {targetAxisIndex: 0, visibleInLegend: false, color: 'black'}
          } )
        },
        tooltip: {
          isHtml: true
        },
        curveType: 'function'
      };
    }
  },
  methods: {
    // rows = [date, upVotes, tooltip, downVotes, tooltip, sentiment, tooltip, monthSelector, monthTooltip];
    convertVotesToFractionOfMaxVotes(rows = []) {
      let maxVotes = Math.max(...rows.map(row => {
        return Math.max(row[1], row[3]);
      })) / .85;
      return rows.map(row => {
        row[1] = row[1] / maxVotes;
        row[3] = row[3] / maxVotes;
        // Edit non month tooltips
        row[2] = this.filterOutExtraTooltipData(row[2]);
        row[4] = this.filterOutExtraTooltipData(row[4]);
        row[6] = this.filterOutExtraTooltipData(row[6]);
        return row;
      });
    },
    filterOutExtraTooltipData(tooltip) {
      // Remove everything between the only `<hr>` and the last `</strong></div>`
      tooltip = tooltip.replace(/<hr>.*<\/strong><\/div>/, '');
      // Add '.votes-tooltip--simplified' to `.votes-tooltip`. `votes-tooltip` appears more than once, but only one time without any `-`'s around it
      tooltip = tooltip.replace(/votes-tooltip(?![\w-])/g, 'votes-tooltip votes-tooltip--simplified');
      return tooltip;
    },
    getRow(tableRef, chartData, dataIndex) {
      try {
        const tableSelection = tableRef.getSelection();
        if (!tableSelection?.length) {
          return;
        }
        const selection = chartData[tableSelection[0].row + 1];
        if (dataIndex === undefined || selection[dataIndex] > 0) { // only select dates with values
          this.$emit('rowSelected', selection);
        }
      }
      catch (e) {
        console.error(e, 'Failed to get row');
      }
    }
  }
};
</script>

<style lang="postcss">
.votes-chart-container {
  @apply relative;

  .google-visualization-tooltip {
    border: none !important;
    background: rgba(255, 255, 255, 0.75);
    box-shadow: 0 0 5px rgba(0, 0, 0, 0.25);
    backdrop-filter: blur(10px);
    border-radius: 5px;
    font-style: normal;
    font-size: 13px;
    line-height: 15px;
    letter-spacing: -0.01em;

    .votes-tooltip {
      hr {
        margin: 2px 0 2px;
      }

      &.votes-tooltip--simplified {
        .votes-tooltip-body {
          padding: 9px 10px 10px;
        }
      }

      .votes-tooltip-body {
        padding: 4px 10px 10px;

        > div {
          @apply w-full flex items-center justify-between;
        }
      }
    }
  }

  .dropdowns {
    width: 100px;
    height: 30px;
    position: absolute;
    top: 20px;
    left: 10px;
  }
}

.all-votes-chart {
  @apply mx-6;

  &, svg {
    overflow: visible !important;
  }

  .votes-tooltip {
    width: 125px;
    height: 38px;
  }
}

.votes-chart {
  rect[fill='#000000'] {
    + g rect {
      width: 2px;
    }
  }

  rect[fill='#000000'] {
    width: 2px;
  }

  .votes-tooltip {
    width: 175px;
    height: 95px;

    &.votes-tooltip--simplified {
      height: 55px;
    }

    header {
      @apply bg-black text-white flex items-center justify-between;
      box-shadow: 0 0 5px rgba(0, 0, 0, 0.25);
      border-radius: 5px 5px 0 0;
      padding: 4px 10px;
    }
  }
}
</style>